/**
 * Qué guía ve cada operador.
 *
 * Módulo puro y probado a propósito. El defecto que existe para evitar es el
 * que el manual documenta capítulo tras capítulo: ofrecer algo que la
 * autorización va a negar. Enseñarle a un `SUPPORT_AGENT` a asignar tickets
 * —que es de `SUPPORT_LEAD`— no es una ayuda incompleta, es una mentira.
 *
 * La fuente de verdad de quién llega a dónde es `navigationByRole`, la misma
 * que pinta el menú. Aquí no se re-declara: se consulta (`roleAccess.ts`).
 */

import { roleCanReach, roleHasCountryNav } from './roleAccess';
import type { TourContext, TourFlow, TourStep } from './types';

/**
 * Los pasos que este operador puede ver, en orden.
 *
 * Un paso sin `roles` lo ve todo el mundo; con ellos, sólo quien esté en la
 * lista. Sin rol en el contexto —sesión sin perfil de staff— los pasos
 * acotados desaparecen.
 */
export function selectSteps(flow: TourFlow, ctx: TourContext): TourStep[] {
  return flow.steps.filter((step) => {
    if (step.requiresCountryNav && !roleHasCountryNav(ctx.role)) return false;
    if (!step.roles) return true;
    return ctx.role !== undefined && step.roles.includes(ctx.role);
  });
}

/**
 * Si esta guía se le ofrece a este operador.
 *
 * Cuatro cortes, en este orden:
 *   1. **Alcance del menú.** Si la guía declara `href`, el rol tiene que
 *      tenerlo en su menú. Si declara `roles`, tiene que estar en la lista.
 *   2. **País.** Casi todo lo operativo cuelga del país seleccionado; sin él
 *      esas pantallas enseñan su estado vacío y no hay nada que señalar.
 *   3. **Que quede algún paso** tras filtrar por rol. Una guía vacía es peor
 *      que ninguna guía.
 */
export function isFlowAvailable(flow: TourFlow, ctx: TourContext): boolean {
  if (flow.href && !roleCanReach(ctx.role, flow.href)) return false;

  if (flow.roles && (ctx.role === undefined || !flow.roles.includes(ctx.role))) {
    return false;
  }

  if (flow.requiresCountry && !ctx.countryId) return false;

  return selectSteps(flow, ctx).length > 0;
}

/** Las guías disponibles, conservando el orden del registro. */
export function selectFlows(flows: readonly TourFlow[], ctx: TourContext): TourFlow[] {
  return flows.filter((flow) => isFlowAvailable(flow, ctx));
}

/**
 * La guía asociada a una ruta, si la hay.
 *
 * Alimenta el botón de ayuda de la esquina. Se compara contra el `href` de
 * la guía, que es la pantalla de la que trata.
 *
 * **Las subrutas cuentan.** El menú lista `/companies`, pero el operador
 * pasa la mitad del tiempo en `/companies/:id`: con una comparación exacta,
 * el botón desaparecía justo al entrar al detalle, que es donde más falta
 * hace. Se acepta el prefijo, y sólo en el corte de la barra — `/companies`
 * no puede reclamar `/companies-suspendidas`.
 *
 * Si dos guías casan, gana **la más específica**: una guía del detalle
 * manda sobre la del listado del que cuelga. Hoy no llega a ocurrir —ninguna
 * ruta del menú es prefijo de otra— pero es la regla que hace falta el día
 * que una guía cuelgue de una subruta, y sin ella el orden lo decidiría el
 * registro.
 */
export function findFlowForPath(
  flows: readonly TourFlow[],
  ctx: TourContext,
  pathname: string,
): TourFlow | undefined {
  const candidatas = selectFlows(flows, ctx).filter(
    (flow) =>
      flow.href !== undefined &&
      (pathname === flow.href || pathname.startsWith(`${flow.href}/`)),
  );

  return candidatas.sort(
    (a, b) => (b.href as string).length - (a.href as string).length,
  )[0];
}
